<template>
  <NavBar v-if="userLoaded" pageName="Services"></NavBar>
  <AddNewService v-if="showAddNewService" :showAddNewService="showAddNewService" :clientsHierarchy="clientsHierarchy" @newServiceAddedEvent="newServiceAdded" @closeAddNewClientEvent="closeAddNewClient"></AddNewService>
  <EditService v-if="showEditService" :service="selectedService" @serviceUpdatedEvent="serviceUpdated" @closeEditServiceEvent="closeEditService"></EditService>

  <!-- Activate Service (Add Client Service) Dialog -->
  <AddNewClientService
    v-if="showActivateServiceDialog"
    :show="showActivateServiceDialog"
    :renew-data="activateServiceData"
    @close="closeActivateServiceDialog"
    @serviceAdded="onServiceActivated"
  />

  <!-- Terminate Service Dialog -->
  <v-dialog v-model="showTerminateDialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="text-h6 bg-error text-white">
        Terminate Service
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert type="warning" variant="tonal" class="mb-4">
          Are you sure you want to terminate the service <strong>{{ terminateServiceData?.name }}</strong>?
        </v-alert>

        <p class="mb-2">After termination:</p>
        <ul class="mb-4">
          <li>No new Client Services can be created based on this service</li>
          <li>The service will be marked as "Terminated"</li>
        </ul>

        <v-alert v-if="terminateServiceData?.active_cs_count > 0" type="info" variant="tonal" class="mb-4">
          This service has <strong>{{ terminateServiceData?.active_cs_count }}</strong> active Client Services.
        </v-alert>

        <v-checkbox
          v-model="terminateExistingCs"
          :disabled="!terminateServiceData?.active_cs_count"
          color="error"
          hide-details
        >
          <template v-slot:label>
            <span :class="terminateExistingCs ? 'text-error font-weight-bold' : ''">
              Also terminate all existing Client Services ({{ terminateServiceData?.active_cs_count || 0 }} active)
            </span>
          </template>
        </v-checkbox>

        <v-alert v-if="terminateExistingCs" type="error" variant="tonal" class="mt-4">
          <strong>Warning:</strong> This will terminate all active Client Services, including any associated SIMs and MSISDNs. This action cannot be undone!
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeTerminateDialog">{{ t.common.cancel }}</v-btn>
        <v-btn color="error" variant="flat" :loading="terminateLoading" @click="confirmTerminateService">
          Terminate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar for messages -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000">
    {{ snackbar.message }}
  </v-snackbar>

  <v-container style="margin-top: 185px;">
    <h2 class="text-h4 mb-4">Services Management</h2>
    <!-- Search and Actions -->
    <v-row class="align-center mb-2">
      <v-col cols="12" md="4">
        <v-text-field
          ref="filterServiceBy"
          :label="t.common.filter"
          v-model="filterText"
          :placeholder="t.common.search"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          @click="$event.target.select()"
          @dblclick="filterText = ''"
        />
      </v-col>
      <v-col cols="12" md="8" class="d-flex justify-end ga-2">
        <v-btn color="secondary" variant="outlined" :loading="loading" @click="resetAllFilters">
          {{ t.common.reset }}
        </v-btn>
        <v-btn color="success" @click="addNewService">
          {{ t.services.add_new_service }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card class="mb-4">
      <v-card-text class="py-2">
        <!-- Client Hierarchy Filter -->
        <ClientSearchFilter
          v-model="selectedClientId"
          v-model:include-subclients="includeSubclientsToggle"
          :clients-hierarchy="clientsHierarchy"
          client-select-label="Managed By Client"
          @update:model-value="loadAvailableServices"
          @update:include-subclients="loadAvailableServices"
        />

        <!-- Service Type Filter -->
        <v-row dense align="center" class="mb-2">
          <v-col cols="12">
            <ServiceTypeButtonFilter v-model="serviceTypeFilter" />
          </v-col>
        </v-row>

        <v-row dense align="center">
          <!-- Service Status Filter -->
          <v-col cols="12" md="3">
            <div class="text-caption font-weight-bold mb-1">Status</div>
            <v-btn-toggle v-model="filters.serviceStatus" multiple density="compact" color="primary">
              <v-btn value="active" size="small">Active</v-btn>
              <v-btn value="test" size="small">Test</v-btn>
              <v-btn value="terminated" size="small">Terminated</v-btn>
            </v-btn-toggle>
          </v-col>

          <!-- Billing Type Filter -->
          <v-col cols="12" md="2">
            <div class="text-caption font-weight-bold mb-1">{{ t.services.billing_type }}</div>
            <v-btn-toggle v-model="filters.billingType" multiple density="compact" color="primary">
              <v-btn value="prepaid" size="small">Prepaid</v-btn>
              <v-btn value="postpaid" size="small">Postpaid</v-btn>
            </v-btn-toggle>
          </v-col>

          <!-- Applied to Client Type Filter -->
          <v-col cols="12" md="3">
            <div class="text-caption font-weight-bold mb-1">{{ t.services.applied_to_client_type }}</div>
            <v-btn-toggle v-model="filters.appliedToClientType" multiple density="compact" color="primary">
              <v-btn value="endUser" size="small">EndUser</v-btn>
              <v-btn value="uniLevel" size="small">UniLevel</v-btn>
              <v-btn value="multiLevel" size="small">MultiLevel</v-btn>
            </v-btn-toggle>
          </v-col>

          <!-- Boolean Filters -->
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-bold mb-1">Options</div>
            <div class="d-flex flex-wrap ga-2">
              <v-chip
                :color="filters.selfApplied === true ? 'primary' : 'default'"
                variant="outlined"
                size="small"
                @click="toggleFilter('selfApplied')"
              >
                {{ t.services.self_applied }}: {{ filters.selfApplied === null ? 'All' : (filters.selfApplied ? 'Yes' : 'No') }}
              </v-chip>
              <v-chip
                :color="filters.allowMultiple === true ? 'primary' : 'default'"
                variant="outlined"
                size="small"
                @click="toggleFilter('allowMultiple')"
              >
                {{ t.services.allow_multiple }}: {{ filters.allowMultiple === null ? 'All' : (filters.allowMultiple ? 'Yes' : 'No') }}
              </v-chip>
              <v-chip
                :color="filters.availableToSubclients === true ? 'primary' : 'default'"
                variant="outlined"
                size="small"
                @click="toggleFilter('availableToSubclients')"
              >
                {{ t.services.available_to_subclients }}: {{ filters.availableToSubclients === null ? 'All' : (filters.availableToSubclients ? 'Yes' : 'No') }}
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <!-- Clear Filters -->
        <v-row dense class="mt-1">
          <v-col cols="12" class="d-flex justify-end">
            <v-btn variant="text" size="small" color="secondary" @click="clearFilters">
              Clear Filters
            </v-btn>
            <span class="ml-4 text-caption align-self-center">
              Showing {{ filteredResults.length }} of {{ servicesList.length }} services
            </span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- Services List -->
  <v-container>
    <v-row v-if="servicesListLoaded">
      <v-col
        v-for="service in filteredResults"
        :key="service.id"
        cols="12"
      >
        <div v-if="showDebugInfo">{{ service }}</div>
        <v-card style="max-width: 900px;">
          <v-card-title class="text-h6">
            {{ service.public_name || service.name }}
            <v-chip v-if="service.service_status_name" size="small" class="ml-2" :color="getStatusColor(service.service_status_name)">
              {{ service.service_status_name }}
            </v-chip>
          </v-card-title>
          <v-card-subtitle>
            ID: {{ service.id }} - {{ service.description }}
            <span v-if="service.nickname" class="ml-2">({{ service.nickname }})</span>
          </v-card-subtitle>
          <v-card-text>
            <!-- CS Counts -->
            <v-row dense class="mb-2">
              <v-col cols="12">
                <v-chip size="small" color="success" class="mr-2">
                  Active CS: {{ service.active_cs_count || 0 }}
                </v-chip>
                <v-chip size="small" color="warning">
                  Expired CS: {{ service.expired_cs_count || 0 }}
                </v-chip>
              </v-col>
            </v-row>

            <!-- All Info in specified order -->
            <v-row dense>
              <v-col cols="6"><strong>{{ t.users.app }}:</strong> {{ service.app_name || '-' }}</v-col>
              <v-col cols="6"><strong>Managed By:</strong> {{ isManagedBySelf(service.managed_by_client_id) ? "Myself - " + (user?.userClient?.name || '') : (service.managed_by_client_name || '-') }}</v-col>
              <v-col cols="6"><strong>{{ t.services.service_type }}:</strong> {{ service.service_type_name || '-' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.applied_to_client_type }}:</strong> {{ service.applied_to_client_type_name || '-' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.available_to_subclients }}:</strong> {{ service.available_to_subclients ? t.common.yes : t.common.no }}</v-col>
              <v-col cols="6"><strong>{{ t.services.self_applied }}:</strong> {{ service.self_applied ? t.common.yes : t.common.no }}</v-col>
              <v-col cols="6"><strong>{{ t.services.allow_multiple }}:</strong> {{ service.allow_multiple ? t.common.yes : t.common.no }}</v-col>
              <v-col cols="6"><strong>{{ t.services.billing_type }}:</strong> {{ service.billing_type_name || '-' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.billing_period }}:</strong> {{ service.billing_period_name || '-' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.price }}:</strong> {{ service.service_price || 0 }} {{ service.currency_name || '' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.validity }}:</strong> {{ service.validity || '-' }}</v-col>
              <v-col cols="6"><strong>{{ t.services.payment_methods }}:</strong> {{ service.payment_method_type_name || '-' }}</v-col>
              <v-col cols="6"><strong>MVNO_ID:</strong> {{ service.mvno_id || '-' }}</v-col>
              <v-col cols="6"><strong>Created By:</strong> {{ service.created_by_user_name || '-' }}</v-col>
              <v-col cols="6"><strong>Price Updated:</strong> {{ formatDate(service.service_price_created_at) }}</v-col>
              <v-col cols="6"><strong>{{ t.common.created_at }}:</strong> {{ formatDate(service.created_at) }}</v-col>
              <v-col cols="6"><strong>{{ t.common.updated_at }}:</strong> {{ formatDate(service.updated_at) }}</v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" size="small" :disabled="!canEditService(service)" @click="editService(service)">{{ t.common.edit }}</v-btn>
            <v-btn
              v-if="canActivateService(service)"
              color="success"
              size="small"
              @click="openActivateServiceDialog(service)"
            >
              <v-icon start size="small">mdi-play-circle</v-icon>
              Activate
            </v-btn>
            <v-btn
              v-if="canEditService(service) && service.service_status_name?.toLowerCase() !== 'terminated'"
              color="error"
              size="small"
              variant="outlined"
              @click="openTerminateDialog(service)"
            >
              Terminate
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- No results -->
    <v-row v-if="servicesListLoaded && filteredResults.length === 0">
      <v-col cols="12">
        <v-alert type="info">
          No services match the current filters.
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import { log } from "@/composables/useLog";
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { getAllServicesList, terminateService } from "@/composables/useGetServicesAPI"
import AddNewService from "@/components/AddNewService.vue";
import EditService from "@/components/EditService.vue";
import AddNewClientService from "@/components/AddNewClientService.vue";
import ServiceTypeButtonFilter from "@/components/ServiceTypeButtonFilter.vue";
import ClientSearchFilter from "@/components/ClientSearchFilter.vue";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'ServicesList',
  components: {
    AddNewService,
    EditService,
    AddNewClientService,
    NavBar,
    ServiceTypeButtonFilter,
    ClientSearchFilter
  },
  data() {
    return {
      loading: false,
      showToken: false,
      client_id: store.getters.getUser.client_id,
      totalItems: 0,
      servicesList: [],
      token: '',
      servicesListLoaded: false,
      showDebugInfo: false,
      showAddNewService: false,
      showEditService: false,
      selectedService: null,
      filterText: "",
      user: null,
      userLoaded: false,
      // Client hierarchy filter
      clientsHierarchy: [],
      hasSubclients: false,
      selectedClientId: null,
      includeSubclientsToggle: false,
      // Service type filter (single select, 'all' or ID string)
      serviceTypeFilter: 'all',
      // Filters
      filters: {
        serviceStatus: ['active', 'test'],  // Default: show active and test
        billingType: [],  // Empty = all
        appliedToClientType: [],  // Empty = all
        selfApplied: null,  // null = all, true = yes, false = no
        allowMultiple: null,
        availableToSubclients: null
      },
      // Activate service dialog
      showActivateServiceDialog: false,
      activateServiceData: null,
      // Terminate dialog
      showTerminateDialog: false,
      terminateServiceData: null,
      terminateExistingCs: false,
      terminateLoading: false,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  methods: {
    closeAddNewClient() {
      this.showAddNewService = false
    },
    showServicesList() {
      this.servicesListLoaded = true
    },
    loadAvailableServices() {
      this.loading = true
      const token = store.getters.getToken

      // Build options for API call
      const options = {
        include_subclients: this.includeSubclientsToggle
      }
      if (this.selectedClientId) {
        options.subclient_id = this.selectedClientId
      }

      setTimeout(() => {
        getAllServicesList(token, this.client_id, options).then((response) => {
          log(response)
          this.loading = false
          this.servicesList = response.services_list;
          this.servicesListLoaded = true

          // Load hierarchical clients list from response
          if (response.filters) {
            if (this.clientsHierarchy.length === 0) {
              this.clientsHierarchy = response.filters.clients_hierarchy || []
            }
            this.hasSubclients = response.filters.has_subclients || false
          }

          if (this.$refs.filterServiceBy) {
            this.$refs.filterServiceBy.focus()
          }
        })
      }, 100)
    },
    isManagedBySelf(client_id) {
      return client_id === this.client_id;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },
    getStatusColor(statusName) {
      const colors = {
        'active': 'success',
        'test': 'info',
        'terminated': 'error'
      };
      return colors[statusName?.toLowerCase()] || 'grey';
    },
    newServiceAdded() {
      this.loadAvailableServices()
    },
    addNewService() {
      this.showAddNewService = true
    },
    editService(service) {
      this.selectedService = { ...service };
      this.showEditService = true;
    },
    closeEditService() {
      this.showEditService = false;
      this.selectedService = null;
    },
    serviceUpdated() {
      this.closeEditService();
      this.loadAvailableServices();
    },
    toggleFilter(filterName) {
      // Cycle through: null (all) -> true (yes) -> false (no) -> null
      if (this.filters[filterName] === null) {
        this.filters[filterName] = true;
      } else if (this.filters[filterName] === true) {
        this.filters[filterName] = false;
      } else {
        this.filters[filterName] = null;
      }
    },
    clearFilters() {
      this.serviceTypeFilter = 'all';
      this.filters = {
        serviceStatus: ['active', 'test'],
        billingType: [],
        appliedToClientType: [],
        selfApplied: null,
        allowMultiple: null,
        availableToSubclients: null
      };
      this.filterText = "";
      this.selectedClientId = null;
      this.includeSubclientsToggle = false;
      this.loadAvailableServices();
    },
    resetAllFilters() {
      // Reset all filters to default and load all services
      this.serviceTypeFilter = 'all';
      this.filters = {
        serviceStatus: ['active', 'test'],
        billingType: [],
        appliedToClientType: [],
        selfApplied: null,
        allowMultiple: null,
        availableToSubclients: null
      };
      this.filterText = "";
      this.selectedClientId = null;
      this.includeSubclientsToggle = false;
      // Load all services without client filtering
      this.loadAvailableServicesDefault();
    },
    loadAvailableServicesDefault() {
      // Load all services without any client filtering (for reset)
      this.loading = true
      const token = store.getters.getToken

      setTimeout(() => {
        getAllServicesList(token, this.client_id, {}).then((response) => {
          log(response)
          this.loading = false
          this.servicesList = response.services_list;
          this.servicesListLoaded = true

          // Load hierarchical clients list from response
          if (response.filters) {
            this.clientsHierarchy = response.filters.clients_hierarchy || []
            this.hasSubclients = response.filters.has_subclients || false
          }

          if (this.$refs.filterServiceBy) {
            this.$refs.filterServiceBy.focus()
          }
        })
      }, 100)
    },
    canEditService(service) {
      if (this.user && this.user.admin) return true
      return service.managed_by_client_id === this.client_id
    },
    canActivateService(service) {
      const status = service.service_status_name?.toLowerCase()
      // Only active/test services can be activated
      return status === 'active' || status === 'test'
    },
    openActivateServiceDialog(service) {
      this.activateServiceData = {
        serviceId: service.id,
        serviceTypeName: service.service_type_name,
        description: ''
      }
      this.showActivateServiceDialog = true
    },
    closeActivateServiceDialog() {
      this.showActivateServiceDialog = false
      this.activateServiceData = null
    },
    onServiceActivated() {
      this.closeActivateServiceDialog()
      this.loadAvailableServices()
    },
    openTerminateDialog(service) {
      this.terminateServiceData = { ...service };
      this.terminateExistingCs = false;
      this.showTerminateDialog = true;
    },
    closeTerminateDialog() {
      this.showTerminateDialog = false;
      this.terminateServiceData = null;
      this.terminateExistingCs = false;
    },
    async confirmTerminateService() {
      if (!this.terminateServiceData) return;

      this.terminateLoading = true;
      try {
        const result = await terminateService(
          this.token,
          this.terminateServiceData.id,
          this.terminateExistingCs
        );

        if (result.error) {
          this.snackbar = {
            show: true,
            message: result.message || 'Error terminating service',
            color: 'error'
          };
        } else {
          this.snackbar = {
            show: true,
            message: result.message || 'Service terminated successfully',
            color: 'success'
          };
          this.closeTerminateDialog();
          this.loadAvailableServices();
        }
      } catch (err) {
        log(err);
        this.snackbar = {
          show: true,
          message: 'Error terminating service',
          color: 'error'
        };
      } finally {
        this.terminateLoading = false;
      }
    }
  },
  created() {
    const { getLoggedIn, user } = useGetLoggedIn()
    getLoggedIn().then(() => {
      log("autorizare in Services after getLoggedIn")
      log("user:")
      log(user.value)
      this.token = store.getters.getToken
      this.user = user.value
      this.userLoaded = true
      this.loadAvailableServices()
    })
  },
  computed: {
    t() {
      return tRef.value;
    },
    filteredResults() {
      let results = this.servicesList;

      // Text filter
      if (this.filterText) {
        const filter = this.filterText.toLowerCase();
        results = results.filter(
          (item) =>
            (item.service_app_name && item.service_app_name.toLowerCase().includes(filter)) ||
            (item.description && item.description.toLowerCase().includes(filter)) ||
            (item.service_type_name && item.service_type_name.toLowerCase().includes(filter)) ||
            (item.managed_by_client_name && item.managed_by_client_name.toLowerCase().includes(filter)) ||
            (item.name && item.name.toLowerCase().includes(filter))
        );
      }

      // Service Status filter
      if (this.filters.serviceStatus.length > 0) {
        results = results.filter(item =>
          this.filters.serviceStatus.includes(item.service_status_name?.toLowerCase())
        );
      }

      // Billing Type filter
      if (this.filters.billingType.length > 0) {
        results = results.filter(item =>
          this.filters.billingType.includes(item.billing_type_name?.toLowerCase())
        );
      }

      // Service Type filter
      if (this.serviceTypeFilter !== 'all') {
        results = results.filter(item =>
          item.service_type_id === parseInt(this.serviceTypeFilter)
        );
      }

      // Applied to Client Type filter
      if (this.filters.appliedToClientType.length > 0) {
        results = results.filter(item =>
          this.filters.appliedToClientType.includes(item.applied_to_client_type_name)
        );
      }

      // Self Applied filter
      if (this.filters.selfApplied !== null) {
        results = results.filter(item => item.self_applied === this.filters.selfApplied);
      }

      // Allow Multiple filter
      if (this.filters.allowMultiple !== null) {
        results = results.filter(item => item.allow_multiple === this.filters.allowMultiple);
      }

      // Available to Subclients filter
      if (this.filters.availableToSubclients !== null) {
        results = results.filter(item => item.available_to_subclients === this.filters.availableToSubclients);
      }

      return results;
    }
  }
});
</script>

<style scoped>
.ga-2 {
  gap: 8px;
}
</style>
